import React, { useEffect, useMemo, useState } from "react";
import { Drawer, Empty, Progress, Spin, Table, Tag, Tooltip, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { threeMReportApis } from "../../../../api/ThreeMReport";
import { fmtINR, fmtINRCompact, fmtQty } from "./materialShared";
import { useWbsName } from "../../shared/useWbsCodes";

// Per-material task drill-down: for ONE material, its cost broken down
// phase → activity → task (three nested ranked tables). Data comes from the
// material-breakdown endpoint as flat rows; grouping happens client-side.
// Respects the page's current phase/activity filters.

interface BreakdownRow {
  phaseId: number;
  phaseName: string;
  activityId: number;
  activityName: string;
  taskId: number;
  taskName: string;
  taskSequenceId?: string;
  totalQuantity?: string;
  totalCost?: string;
}

interface TaskRow {
  key: number;
  rank: number;
  name: string;
  sequenceId?: string;
  qty: number;
  cost: number;
  share: number; // % of parent activity cost
}

interface ActivityRow {
  key: number;
  rank: number;
  name: string;
  qty: number;
  cost: number;
  share: number; // % of parent phase cost
  tasks: TaskRow[];
}

interface PhaseRow {
  key: number;
  rank: number;
  name: string;
  qty: number;
  cost: number;
  share: number; // % of the material's total cost
  activities: ActivityRow[];
}

const costCell = (cost: number) => (
  <Tooltip title={fmtINR(cost)}>
    <span
      style={{
        fontWeight: 600,
        fontVariantNumeric: "tabular-nums",
        cursor: "default",
      }}
    >
      {fmtINRCompact(cost)}
    </span>
  </Tooltip>
);

const qtyCell = (qty: number) => (
  <span style={{ fontVariantNumeric: "tabular-nums" }}>{fmtQty(qty)}</span>
);

const shareCell = (share: number) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
    <Progress
      percent={Math.min(100, Math.round(share * 10) / 10)}
      size="small"
      strokeWidth={4}
      showInfo={false}
      strokeColor="#1677ff"
      style={{ flex: 1, minWidth: 60, margin: 0 }}
    />
    <span
      style={{
        fontSize: 12,
        color: "#595959",
        fontVariantNumeric: "tabular-nums",
        width: 42,
        textAlign: "right",
      }}
    >
      {share.toFixed(1)}%
    </span>
  </div>
);

const rankCell = (rank: number) => (
  <span style={{ color: "#8c8c8c" }}>{rank}</span>
);

const MaterialTaskBreakdownDrawer: React.FC<{
  open: boolean;
  projectId?: number | string;
  materialId?: number;
  materialName?: string;
  uomName?: string;
  phaseId?: number | string;
  activityId?: number | string;
  onClose: () => void;
}> = ({
  open,
  projectId,
  materialId,
  materialName,
  uomName,
  phaseId,
  activityId,
  onClose,
}) => {
  const wbsName = useWbsName();
  const [rows, setRows] = useState<BreakdownRow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !projectId || !materialId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setRows([]);
        const response = await threeMReportApis.get3MMaterialTaskBreakdown(
          projectId,
          materialId,
          { phaseId, activityId },
        );
        if (cancelled) return;
        if (response?.status) {
          setRows(response.data?.rows || []);
        } else {
          message.error(
            response?.message || "Failed to load the material task breakdown",
          );
        }
      } catch (err) {
        console.error("Error loading material task breakdown:", err);
        if (!cancelled)
          message.error("Failed to load the material task breakdown");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, projectId, materialId, phaseId, activityId]);

  // Flat rows → phase → activity → task tree, ranked by cost at every level.
  const phaseRows = useMemo<PhaseRow[]>(() => {
    const phaseMap = new Map<
      number,
      {
        name: string;
        activities: Map<number, { name: string; tasks: BreakdownRow[] }>;
      }
    >();
    rows.forEach((row) => {
      if (!phaseMap.has(row.phaseId)) {
        phaseMap.set(row.phaseId, { name: row.phaseName, activities: new Map() });
      }
      const phase = phaseMap.get(row.phaseId)!;
      if (!phase.activities.has(row.activityId)) {
        phase.activities.set(row.activityId, {
          name: row.activityName,
          tasks: [],
        });
      }
      phase.activities.get(row.activityId)!.tasks.push(row);
    });

    const phases = Array.from(phaseMap.entries()).map(([phaseId, phase]) => {
      const activities = Array.from(phase.activities.entries()).map(
        ([activityId, activity]) => {
          const tasks = activity.tasks
            .map((t) => ({
              taskId: t.taskId,
              name: t.taskName,
              sequenceId: t.taskSequenceId,
              qty: Number(t.totalQuantity) || 0,
              cost: Number(t.totalCost) || 0,
            }))
            .sort((a, b) => b.cost - a.cost);
          const cost = tasks.reduce((s, t) => s + t.cost, 0);
          const qty = tasks.reduce((s, t) => s + t.qty, 0);
          return { activityId, name: activity.name, cost, qty, tasks };
        },
      );
      const cost = activities.reduce((s, a) => s + a.cost, 0);
      const qty = activities.reduce((s, a) => s + a.qty, 0);
      return { phaseId, name: phase.name, cost, qty, activities };
    });

    const materialTotal = phases.reduce((s, p) => s + p.cost, 0);
    return phases
      .sort((a, b) => b.cost - a.cost)
      .map((p, pIndex) => ({
        key: p.phaseId,
        rank: pIndex + 1,
        name: p.name,
        qty: p.qty,
        cost: p.cost,
        share: materialTotal > 0 ? (p.cost / materialTotal) * 100 : 0,
        activities: p.activities
          .sort((a, b) => b.cost - a.cost)
          .map((a, aIndex) => ({
            key: a.activityId,
            rank: aIndex + 1,
            name: a.name,
            qty: a.qty,
            cost: a.cost,
            share: p.cost > 0 ? (a.cost / p.cost) * 100 : 0,
            tasks: a.tasks.map((t, tIndex) => ({
              key: t.taskId,
              rank: tIndex + 1,
              name: t.name,
              sequenceId: t.sequenceId,
              qty: t.qty,
              cost: t.cost,
              share: a.cost > 0 ? (t.cost / a.cost) * 100 : 0,
            })),
          })),
      }));
  }, [rows]);

  const materialTotal = useMemo(
    () => phaseRows.reduce((s, p) => s + p.cost, 0),
    [phaseRows],
  );

  const qtyTitle = uomName ? `Qty (${uomName})` : "Qty";

  const taskColumns: ColumnsType<TaskRow> = [
    { title: "#", dataIndex: "rank", width: 48, render: rankCell },
    {
      title: "Task",
      dataIndex: "name",
      render: (name: string, row) => (
        <>
          {row.sequenceId ? (
            <div style={{ fontSize: 11, color: "#8c8c8c" }}>{row.sequenceId}</div>
          ) : null}
          <span style={{ fontWeight: 500 }}>{name}</span>
        </>
      ),
    },
    {
      title: qtyTitle,
      dataIndex: "qty",
      width: 110,
      align: "right",
      render: qtyCell,
    },
    { title: "Cost", dataIndex: "cost", width: 120, align: "right", render: costCell },
    { title: "% of Activity", dataIndex: "share", width: 150, render: shareCell },
  ];

  const activityColumns: ColumnsType<ActivityRow> = [
    { title: "#", dataIndex: "rank", width: 48, render: rankCell },
    {
      title: "Activity",
      dataIndex: "name",
      render: (name: string, row) => (
        <>
          <div style={{ fontWeight: 500 }} title={name}>{wbsName(name)}</div>
          <div style={{ fontSize: 11, color: "#8c8c8c" }}>
            {row.tasks.length} {row.tasks.length === 1 ? "task" : "tasks"}
          </div>
        </>
      ),
    },
    {
      title: qtyTitle,
      dataIndex: "qty",
      width: 110,
      align: "right",
      render: qtyCell,
    },
    { title: "Cost", dataIndex: "cost", width: 120, align: "right", render: costCell },
    { title: "% of Phase", dataIndex: "share", width: 150, render: shareCell },
  ];

  const phaseColumns: ColumnsType<PhaseRow> = [
    { title: "#", dataIndex: "rank", width: 48, render: rankCell },
    {
      title: "Phase",
      dataIndex: "name",
      render: (name: string, row) => (
        <>
          <div style={{ fontWeight: 500 }} title={name}>{wbsName(name)}</div>
          <div style={{ fontSize: 11, color: "#8c8c8c" }}>
            {row.activities.length}{" "}
            {row.activities.length === 1 ? "activity" : "activities"}
          </div>
        </>
      ),
    },
    {
      title: qtyTitle,
      dataIndex: "qty",
      width: 110,
      align: "right",
      render: qtyCell,
    },
    { title: "Cost", dataIndex: "cost", width: 120, align: "right", render: costCell },
    { title: "% of Material", dataIndex: "share", width: 150, render: shareCell },
  ];

  return (
    <Drawer
      title={
        materialName ? `${materialName} — Task Breakdown` : "Task Breakdown"
      }
      placement="right"
      width="min(900px, 100vw)"
      onClose={onClose}
      open={open}
      destroyOnClose
      footer={
        phaseRows.length > 0 ? (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontWeight: 600,
              color: "#022c57",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            <span>Material Total</span>
            <span>{fmtINR(materialTotal)}</span>
          </div>
        ) : null
      }
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <Spin />
        </div>
      ) : phaseRows.length === 0 ? (
        <Empty description="No task usage found for this material" />
      ) : (
        <>
          <div style={{ marginBottom: 12 }}>
            <Tag color="blue" style={{ fontSize: 12 }}>
              {rows.length} task{rows.length === 1 ? "" : "s"} across{" "}
              {phaseRows.length} phase{phaseRows.length === 1 ? "" : "s"}
            </Tag>
          </div>
          <Table<PhaseRow>
            className="customTable"
            rowKey="key"
            columns={phaseColumns}
            dataSource={phaseRows}
            pagination={false}
            bordered
            size="small"
            expandable={{
              rowExpandable: (record) => record.activities.length > 0,
              expandedRowRender: (record) => (
                <Table<ActivityRow>
                  className="customTable"
                  rowKey="key"
                  columns={activityColumns}
                  dataSource={record.activities}
                  pagination={false}
                  bordered
                  size="small"
                  expandable={{
                    rowExpandable: (activity) => activity.tasks.length > 0,
                    expandedRowRender: (activity) => (
                      <Table<TaskRow>
                        className="customTable"
                        rowKey="key"
                        columns={taskColumns}
                        dataSource={activity.tasks}
                        pagination={false}
                        bordered
                        size="small"
                      />
                    ),
                  }}
                />
              ),
            }}
          />
        </>
      )}
    </Drawer>
  );
};

export default MaterialTaskBreakdownDrawer;
